import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { IDashboardInfoRoutesTruckImplement } from 'app/shared/model/dashboard-truck-implement-info.model';
import { IDashboardTruckImplementDTO } from 'app/shared/model/dto/dashboard-truck-implement-dto.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import axios from 'axios';
import { ITruckImplementInfo } from '../../../shared/model/truck-implemet-info.model';
import { IInfoRoutesByTruckImplement } from './interface/dashboardTruckImplementInterface';

const initialState = {
  loading: false,
  errorMessage: null,
  totalItems: 0,
  dashboardTruckImplementDTO: [] as ReadonlyArray<IDashboardTruckImplementDTO>,
  dashboardTruckImplement: [] as ReadonlyArray<IDashboardTruckImplementDTO>,
  truckImplementInfo: [] as ReadonlyArray<ITruckImplementInfo>,
  infoRoutesTruckImplement: {} as Readonly<IDashboardInfoRoutesTruckImplement>,
  dataExcel: {} as Readonly<Blob>,
  vehicleLoad: {} as any,
  loadingLoad: false,
  errorMessageLoad: null,
  vehiclePressure: {} as any,
  loadingPressure: false,
  errorMessagePressure: null,
  vehicleTemperature: {} as any,
  loadingTemperature: false,
  errorMessageTemperature: null,
};

export type DashboardTruckImplementState = Readonly<typeof initialState>;

const apiUrl = 'api/dashboard-truck-implement';
const apiUrlInfoRoutes = 'api/truck-implements';
const apiUrlTruckImplementInfo = 'api/dashboard-truck-implement-info';
const apiUrlExcelGenerator = 'api/export-to-excel';
const apiUrlLoad = 'api/sensor-data-last/load';
const apiUrlPressure = 'api/sensor-data-last/pressure';
const apiUrlTemperature = 'api/sensor-data-last/temperature';

type ParamsTruckImplementIdPeriodic = { truckImplementId: string | number; periodic: PeriodicType; onDetails?: boolean };
export const getSensorDataByTruckImplementIdAndPeriodic = createAsyncThunk(
  'dashboardTruckImplement/fetch_sensor_data_daily_by_truck_implementId_and_periodic',
  async ({ truckImplementId, periodic, onDetails }: ParamsTruckImplementIdPeriodic) => {
    const requestUrl = `${apiUrl}/${truckImplementId}/${periodic}/?onDetails=${onDetails}`;
    return axios.get<IDashboardTruckImplementDTO[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

type ParamsTruckImplementIdDate = { truckImplementId: string | number; dateStart: string; dateEnd: string };
export const getSensorDataByTruckImplementIdAndDateStartAndDateEnd = createAsyncThunk(
  'dashboardTruckImplement/fetch_sensor_data_daily_by_truck_implementId_and_dateStart_and_dateEnd',
  async ({ truckImplementId, dateStart, dateEnd }: ParamsTruckImplementIdDate) => {
    const requestUrl = `${apiUrl}/${truckImplementId}/${dateStart}/${dateEnd}`;
    return axios.get<IDashboardTruckImplementDTO[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

interface IVehicleLoad {
  loadingLoad: boolean;
  vehicleLoad: any;
  errorMessageLoad: any;
}

export const getDataVehicleLoad = createAsyncThunk(
  'vehicleLoad/fetch_vehicle_load',
  async (vehicleId: string) => {
    const requestUrl = `${apiUrlLoad}/${vehicleId}`;
    return axios.get<IVehicleLoad[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

interface IVehiclePressure {
  loadingPressure: boolean;
  vehiclePressure: any;
  errorMessagePressure: any;
}

export const getDataVehiclePressure = createAsyncThunk(
  'vehiclePressure/fetch_vehicle_pressure',
  async (vehicleId: string) => {
    const requestUrl = `${apiUrlPressure}/${vehicleId}`;
    return axios.get<IVehiclePressure[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

interface IVehicleTemperature {
  loadingTemperature: boolean;
  vehicleTemperature: any;
  errorMessageTemperature: any;
}

export const getDataVehicleTemperature = createAsyncThunk(
  'vehicleTemperature/fetch_vehicle_temperature',
  async (vehicleId: string) => {
    const requestUrl = `${apiUrlTemperature}/${vehicleId}`;
    return axios.get<IVehicleTemperature[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

interface IPagedTruckImplementQueryParams extends IQueryParams {
  plateSearch?: string;
  companySearch?: string;
}
export const getPagedTruckImplementInfo = createAsyncThunk(
  'dashboardTruckImplement/fetch_truck_implement_info',
  async ({ page, size, sort, plateSearch, companySearch }: IPagedTruckImplementQueryParams) => {
    const onPlateSearch = `&plateSearch=${plateSearch?.toUpperCase() ?? ''}`;
    const onCompanySearch = `&companySearch=${companySearch?.toUpperCase() ?? ''}`;
    const isWeb = true; // parâmetro temporário

    const requestUrl = `${apiUrlTruckImplementInfo}?page=${page}&size=${size}&sort=${sort}${onPlateSearch}${onCompanySearch}&isWeb=${isWeb}&cacheBuster=${new Date().getTime()}`;
    return axios.get<ITruckImplementInfo[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getInfoRoutesByTruckImplement = createAsyncThunk(
  'dashboardTruckImplement/fetch_info_routes_truck_implement',
  async ({ truckImplementId, periodicType, dateStartPeriodic, dateEndPeriodic, isBetaVersion }: IInfoRoutesByTruckImplement) => {
    if (dateStartPeriodic && dateEndPeriodic) {
      return axios.get<IDashboardInfoRoutesTruckImplement>(
        `${apiUrlInfoRoutes}${`/routes-summary/${truckImplementId}/${dateStartPeriodic}/${dateEndPeriodic}`}`,
        {
          params: { isBetaVersion },
        }
      );
    }
    return axios.get<IDashboardInfoRoutesTruckImplement>(`${apiUrlInfoRoutes}${`/routes-summary/${truckImplementId}/${periodicType}`}`, {
      params: { isBetaVersion },
    });
  },
  { serializeError: serializeAxiosError }
);

// Fetch a report from a list of sensors.
export interface IReportVehicle {
  startDate: string | null;
  finalDate: string | null;
  vehicleId: number | string;
  sensorIds: string[];
}
export const getReportFromSensorsDataByVehicle = createAsyncThunk(
  'dashboardTruckImplement/fetch_report_from_sensors_data_vehicle',
  async ({ startDate, finalDate, vehicleId, sensorIds }: IReportVehicle) => {
    return axios.get<Blob>(`${apiUrlExcelGenerator}${`/sensors?vehicleId=${vehicleId}&startDate=${startDate}&finalDate=${finalDate}&sensorIds=${sensorIds}`}`, {
      responseType: 'blob',
    });
  },
  { serializeError: serializeAxiosError }
);

interface ISensorDataByTruckImplementByPeriodicBySensorId {
  truckImplementId: number;
  periodic: PeriodicType;
  sensorId: number | string;
}

export const getSensorDataByTruckImplementByPeriodicBySensorId = createAsyncThunk(
  'dashboardTruckImplement/fetch_sensor_data_by_truck_implement_by_periodic_by_sensor_id',
  async ({ truckImplementId, periodic, sensorId }: ISensorDataByTruckImplementByPeriodicBySensorId) => {
    return axios.get<IDashboardTruckImplementDTO[]>(`${apiUrl}${`/by-sensor/${truckImplementId}/${sensorId}/${periodic}`}`);
  }
);

interface ISensorDataByTruckImplementByCustomBySensorId {
  truckImplementId: number;
  dateStart: string;
  dateEnd: string;
  sensorId: number | string;
}
export const getSensorDataByTruckImplementByCustomBySensorId = createAsyncThunk(
  `dashboardTruckImplement/fetch_sensor_data_daily_by_truck_implementId_and_dateStart_and_dateEnd`,
  async ({ truckImplementId, dateStart, dateEnd, sensorId }: ISensorDataByTruckImplementByCustomBySensorId) => {
    const requestUrl = `${apiUrl}/by-sensor/custom/${truckImplementId}/${sensorId}/${dateStart}/${dateEnd}`;
    return axios.get<IDashboardTruckImplementDTO[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const slice = createSlice({
  name: 'dashboardTruckImplement',
  initialState: initialState as DashboardTruckImplementState,
  reducers: {
    reset(state) {
      return {
        ...state,
        dashboardTruckImplement: initialState.dashboardTruckImplement,
        dashboardTruckImplementDTO: state.dashboardTruckImplementDTO,
        loading: initialState.loading,
        errorMessage: initialState.errorMessage,
        totalItems: initialState.totalItems,
        truckImplementInfo: initialState.truckImplementInfo,
        infoRoutesTruckImplement: initialState.infoRoutesTruckImplement,
        dataExcel: initialState.dataExcel,
        vehicleLoad: initialState.vehicleLoad,
        loadingLoad: initialState.loadingLoad,
        errorMessageLoad: initialState.errorMessageLoad,
        vehiclePressure: initialState.vehiclePressure,
        loadingPressure: initialState.loadingPressure,
        errorMessagePressure: initialState.errorMessagePressure,
        vehicleTemperature: initialState.vehicleTemperature,
        loadingTemperature: initialState.loadingTemperature,
        errorMessageTemperature: initialState.errorMessageTemperature,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getSensorDataByTruckImplementIdAndPeriodic, getSensorDataByTruckImplementIdAndDateStartAndDateEnd), (state, action) => {
        return {
          ...state,
          loading: false,
          dashboardTruckImplementDTO: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getDataVehicleLoad), (state, action) => {
        return {
          ...state,
          vehicleLoad: action.payload.data,
          loadingLoad: false,
        };
      })
      .addMatcher(isFulfilled(getDataVehiclePressure), (state, action) => {
        return {
          ...state,
          vehiclePressure: action.payload.data,
          loadingPressure: false,
        };
      })
      .addMatcher(isFulfilled(getDataVehicleTemperature), (state, action) => {
        return {
          ...state,
          vehicleTemperature: action.payload.data,
          loadingTemperature: false,
        };
      })
      .addMatcher(isFulfilled(getPagedTruckImplementInfo), (state, action) => {
        return {
          ...state,
          truckImplementInfo: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
          loading: false,
        };
      })
      .addMatcher(isFulfilled(getInfoRoutesByTruckImplement), (state, action) => {
        return {
          ...state,
          infoRoutesTruckImplement: action.payload.data,
          loading: false,
        };
      })
      .addMatcher(isFulfilled(getReportFromSensorsDataByVehicle), (state, action) => {
        return {
          ...state,
          dataExcel: action.payload.data,
          loading: false,
        };
      })
      .addMatcher(isFulfilled(getSensorDataByTruckImplementByPeriodicBySensorId, getSensorDataByTruckImplementByCustomBySensorId), (state, action) => {
        return {
          ...state,
          loading: false,
          dashboardTruckImplement: action.payload.data,
        };
      })
      .addMatcher(
        isPending(
          getSensorDataByTruckImplementIdAndPeriodic,
          getSensorDataByTruckImplementIdAndDateStartAndDateEnd,
          getPagedTruckImplementInfo,
          getInfoRoutesByTruckImplement,
          getReportFromSensorsDataByVehicle,
          getSensorDataByTruckImplementByPeriodicBySensorId,
          getSensorDataByTruckImplementByCustomBySensorId
        ),
        state => {
          state.errorMessage = null;
          state.loading = true;
        }
      )
      .addMatcher(isPending(getDataVehicleLoad), state => {
        state.errorMessageLoad = null;
        state.loadingLoad = true;
      })
      .addMatcher(isPending(getDataVehiclePressure), state => {
        state.errorMessagePressure = null;
        state.loadingPressure = true;
      })
      .addMatcher(isPending(getDataVehicleTemperature), state => {
        state.errorMessageTemperature = null;
        state.loadingTemperature = true;
      })
      .addMatcher(
        isRejected(
          getSensorDataByTruckImplementIdAndPeriodic,
          getSensorDataByTruckImplementIdAndDateStartAndDateEnd,
          getInfoRoutesByTruckImplement,
          getReportFromSensorsDataByVehicle,
          getSensorDataByTruckImplementByPeriodicBySensorId,
          getSensorDataByTruckImplementByCustomBySensorId
        ),
        () => ({
          ...initialState,
          loading: false,
        })
      )
      .addMatcher(isRejected(getDataVehicleLoad), () => ({
        ...initialState,
        loadingLoad: false,
      }))
      .addMatcher(isRejected(getDataVehiclePressure), () => ({
        ...initialState,
        loadingPressure: false,
      }))
      .addMatcher(isRejected(getDataVehicleTemperature), () => ({
        ...initialState,
        loadingTemperature: false,
      }));
  },
});

export const { reset } = slice.actions;

// Reducer
export default slice.reducer;
